import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import TeamBadge from '../widgets/common/TeamBadge';

function OptionLogo({ option, size = 20 }) {
  if (!option?.logoUrl || !option?.label) return null;
  return <TeamBadge name={option.label} logoUrl={option.logoUrl} size={size} />;
}

function OptionBadge({ option }) {
  if (option.badge === 'live') {
    return (
      <span className="sw-combobox-badge sw-combobox-badge--live">LIVE</span>
    );
  }
  if (option.badge === 'ft') {
    return <span className="sw-combobox-badge sw-combobox-badge--ft">FT</span>;
  }
  if (option.badge === 'upcoming') {
    return <span className="sw-combobox-badge sw-combobox-badge--upcoming">Soon</span>;
  }
  return null;
}

/**
 * Select that opens as a search field + filtered list (for long competition/match lists).
 */
export default function SearchableCombobox({
  label,
  value,
  onChange,
  options = [],
  placeholder = 'Search…',
  disabled = false,
  emptyMessage = 'No matches',
  hint = null,
  className = '',
  showSublabelOnButton = true,
}) {
  const listId = useId();
  const rootRef = useRef(null);
  const inputRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');

  const selected = options.find((o) => o.value === value);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter(
      (o) =>
        o.label.toLowerCase().includes(q) ||
        (o.sublabel && o.sublabel.toLowerCase().includes(q)) ||
        (o.group && o.group.toLowerCase().includes(q))
    );
  }, [options, query]);

  const groups = useMemo(() => {
    const byGroup = new Map();
    for (const o of filtered) {
      const g = o.group || '';
      if (!byGroup.has(g)) byGroup.set(g, []);
      byGroup.get(g).push(o);
    }
    return [...byGroup.entries()];
  }, [filtered]);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) {
        setOpen(false);
        setQuery('');
      }
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  useEffect(() => {
    if (open) {
      const t = setTimeout(() => inputRef.current?.focus(), 0);
      return () => clearTimeout(t);
    }
    setQuery('');
    return undefined;
  }, [open]);

  function pick(option) {
    onChange(option.value);
    setOpen(false);
    setQuery('');
  }

  return (
    <div ref={rootRef} className={`sw-combobox relative min-w-0 ${className}`}>
      {label && <span className="sw-combobox__label">{label}</span>}

      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((v) => !v)}
        className={`sw-combobox__trigger ${open ? 'sw-combobox__trigger--open' : ''}`}
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-controls={listId}
      >
        <span className="min-w-0 flex-1 text-left flex items-center gap-2">
          {selected && <OptionLogo option={selected} size={22} />}
          <span className="min-w-0 flex-1">
          <span className="block truncate font-semibold text-slate-900">
            {selected?.label || placeholder}
          </span>
          {showSublabelOnButton && selected?.sublabel && (
            <span className="block text-[10px] text-slate-500 truncate mt-0.5 font-normal">
              {selected.sublabel}
            </span>
          )}
          </span>
        </span>
        <span className="flex items-center gap-2 shrink-0">
          {selected && <OptionBadge option={selected} />}
          <span className="sw-combobox__chevron" aria-hidden>
            {open ? '▴' : '▾'}
          </span>
        </span>
      </button>

      {open && (
        <div className="sw-combobox__panel" role="listbox" id={listId}>
          <div className="sw-combobox__search-wrap">
            <svg
              className="w-4 h-4 text-slate-400 shrink-0"
              viewBox="0 0 20 20"
              fill="currentColor"
              aria-hidden
            >
              <path
                fillRule="evenodd"
                d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.817A6 6 0 012 8z"
                clipRule="evenodd"
              />
            </svg>
            <input
              ref={inputRef}
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={placeholder}
              className="sw-combobox__search"
              onKeyDown={(e) => {
                if (e.key === 'Escape') {
                  setOpen(false);
                  setQuery('');
                }
              }}
            />
          </div>
          <ul className="sw-combobox__list">
            {groups.length === 0 && (
              <li className="px-4 py-6 text-center text-slate-400 text-xs">{emptyMessage}</li>
            )}
            {groups.map(([group, items]) => (
              <li key={group || '_'}>
                {group && <div className="sw-combobox__group">{group}</div>}
                <ul>
                  {items.map((o) => {
                    const active = o.value === value;
                    return (
                      <li key={o.value || '__auto'}>
                        <button
                          type="button"
                          role="option"
                          aria-selected={active}
                          onClick={() => pick(o)}
                          className={`sw-combobox__option ${active ? 'sw-combobox__option--active' : ''}`}
                        >
                          <span className="min-w-0 flex-1 flex items-center gap-2">
                            <OptionLogo option={o} size={20} />
                            <span className="min-w-0 flex-1">
                            <span className="block font-medium truncate">{o.label}</span>
                            {o.sublabel && (
                              <span className="block text-[10px] text-slate-500 truncate mt-0.5">
                                {o.sublabel}
                              </span>
                            )}
                            </span>
                          </span>
                          <span className="flex items-center gap-1.5 shrink-0 ml-2">
                            <OptionBadge option={o} />
                            {active && (
                              <span className="text-brand-600 text-sm font-bold" aria-hidden>
                                ✓
                              </span>
                            )}
                          </span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </li>
            ))}
          </ul>
        </div>
      )}

      {hint && !open && <p className="sw-combobox__hint">{hint}</p>}
    </div>
  );
}
